import { SINGLETON_DOCUMENT } from '@pkg/config/sanity/singletons';
import { DOCUMENT } from '@pkg/config/sanity/schemas';

export const SINGLETON_DOC_PREFIX = `singleton`;

/**
 * Used to get the singleton document ID for a particular schema type.
 * If a locale code is provided in context, we provide the region-specific document for this singleton.
 */
export function singletonDocId(schemaType: string, localeCode?: string, suffix?: string) {
  let documentId = localeCode
    ? `${SINGLETON_DOC_PREFIX}-${schemaType}-${localeCode}`
    : `${SINGLETON_DOC_PREFIX}-${schemaType}`;

  documentId = !!suffix
    ? `${documentId}-${suffix}`
    : `${documentId}`;

  return documentId
    .replaceAll('.', '_') // Document id's with a dot in are made private by default, so remove that for this utility.
    .replaceAll(' ', '-'); // IDs with spaces in are not valid, so remove them.
}

/**
 * A helper method for getting a singleton document ID that has a suffix.
 * As they are all unique, we can specify the DOCUMENT type in this helper, to make it
 * easier to get a suffixed singleton.
 *
 * If any of the values change (the DOCUMENT types), a new page in the CMS will be created for all locales.
 * The original page will still exist, with the original slug, so may conflict and prevent using the slug again.
 * Therefore, if you change the DOCUMENT type of any below, ensure you also migrate and/or delete the previous page.
 */
export function singletonDocIdByName(singletonDoc: SINGLETON_DOCUMENT, localeCode?: string) {
  return singletonDocId(singletonDocToDocType(singletonDoc), localeCode, singletonDoc);
}

export function singletonDocToDocType(singletonDoc: SINGLETON_DOCUMENT) {
  const toDocTypeMap = {
    [SINGLETON_DOCUMENT.HOMEPAGE]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.BUSINESS_HOMEPAGE]: DOCUMENT.BUSINESS_HOMEPAGE,
    [SINGLETON_DOCUMENT.BUSINESS_PAGE_404]: DOCUMENT.BUSINESS_HOMEPAGE,
    [SINGLETON_DOCUMENT.INITIATIVES_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.INITIATIVE_B2B_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.ACQUISITION_B2B_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.ACQUISITION_B2C_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.ABOUT_FOOD_WASTE_HOME]: DOCUMENT.ABOUT_FOOD_WASTE_PAGES,
    [SINGLETON_DOCUMENT.ABOUT_FOOD_WASTE_BLOG_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.ABOUT_US_HOME]: DOCUMENT.ABOUT_US_PAGES,
    [SINGLETON_DOCUMENT.CAREERS_HOME]: DOCUMENT.CAREER_PAGES,
    [SINGLETON_DOCUMENT.LEGAL_HOME]: DOCUMENT.LEGAL_PAGES,
    [SINGLETON_DOCUMENT.SUPPORT_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.SURPRISE_BAG_HOME]: DOCUMENT.SURPRISE_BAG_HOME,
    [SINGLETON_DOCUMENT.SURPRISE_BAG_PAGES]: DOCUMENT.SURPRISE_BAG_PAGES,
    [SINGLETON_DOCUMENT.PAGE_404]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.BRAND_PROFILE_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.BUSINESS_SOLUTIONS_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.MARKETPLACE_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.PARCELS_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.PLATFORM_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.DATE_LABELLING_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.DONATIONS_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.SOLUTION_FINDER]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.SOLUTION_FINDER_FALLBACK]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.PRESS_HOME]: DOCUMENT.PAGES,
    [SINGLETON_DOCUMENT.BLOG_SINGLE_DEFAULT_BLOCKS]: DOCUMENT.DEFAULT_BLOCKS,
    [SINGLETON_DOCUMENT.PRESS_RELEASE_DEFAULT_BLOCKS]: DOCUMENT.DEFAULT_BLOCKS,
  }

  if (!toDocTypeMap[singletonDoc]) {
    throw new Error(`Singleton Document type '${singletonDoc}' is not mapped to a Document Type - cannot get Singleton Document ID. This is likely an error in the code: please contact TGTG Marketing Development Team so it can be fixed`);
  }

  return toDocTypeMap[singletonDoc];
}

export function documentIsSingleton(documentId: string) {
  return documentId.startsWith(SINGLETON_DOC_PREFIX);
}
